import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import {
  Loader2,
  KanbanSquare,
  RefreshCw,
  Clock,
  CheckCircle2,
  CircleDashed,
  Users,
  CalendarDays,
  Plus,
  Trash2,
  MoreVertical,
  Pencil,
  LayoutGrid,
  BarChart2,
  AlertTriangle,
  Activity,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KANBAN_COLUMNS = [
  {
    id: 'todo',
    title: 'A faire',
    states: ['New'],
    icon: CircleDashed,
    color: 'text-gray-500',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-t-gray-400',
  },
  {
    id: 'in_progress',
    title: 'En cours',
    states: ['Active', 'In Progress'],
    icon: Clock,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-t-blue-500',
  },
  {
    id: 'done',
    title: 'Termine',
    states: ['Done', 'Closed', 'Resolved'],
    icon: CheckCircle2,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-t-green-500',
  },
];

const COLUMN_TARGET_STATE = {
  todo: 'New',
  in_progress: 'Active',
  done: 'Done',
};

const STATES = ['New', 'Active', 'In Progress', 'Resolved', 'Done', 'Closed'];

const WORK_ITEM_TYPE_CLASS = {
  Epic: 'work-item-epic',
  Feature: 'work-item-feature',
  'User Story': 'work-item-userstory',
  Task: 'work-item-task',
  Bug: 'work-item-bug',
};

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

// ─── Sub-components ──────────────────────────────────────────────

function KanbanCard({ item, index, isUpdating, onEdit, onDelete }) {
  return (
    <Draggable draggableId={String(item.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`p-3 rounded-sm border bg-card shadow-sm transition-shadow cursor-grab active:cursor-grabbing ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''
          } ${isUpdating ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Badge
                variant="outline"
                className={`text-[10px] ${WORK_ITEM_TYPE_CLASS[item.work_item_type] || ''}`}
              >
                {item.work_item_type}
              </Badge>
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-mono text-muted-foreground">
                  #{item.id}
                </span>
                {onEdit && onDelete && <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="h-6 w-6 inline-flex items-center justify-center rounded-sm hover:bg-muted"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(item)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Modifier
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => onDelete(item)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>}
              </div>
            </div>

            <p className="text-sm font-medium leading-tight line-clamp-2">
              {item.title}
            </p>

            <div className="flex items-center justify-between pt-1">
              {item.assigned_to ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                          {getInitials(item.assigned_to)}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent>{item.assigned_to}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <div className="h-6" />
              )}

              {(item.estimated_hours > 0 || item.completed_hours > 0) && (
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {item.completed_hours || 0}h / {item.estimated_hours || 0}h
                </span>
              )}
            </div>

            {isUpdating && (
              <div className="flex justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

function KanbanColumn({ column, items, updatingItemId, onEdit, onDelete }) {
  const Icon = column.icon;
  return (
    <Card className={`flex flex-col border-t-4 ${column.borderColor}`}>
      <CardHeader className="pb-3 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Icon className={`h-4 w-4 ${column.color}`} />
            {column.title}
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            {items.length}
          </Badge>
        </div>
      </CardHeader>
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <CardContent
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 min-h-[150px] space-y-2 p-3 pt-0 transition-colors rounded-b-sm ${
              snapshot.isDraggingOver ? column.bgColor : ''
            }`}
          >
            {items.map((item, index) => (
              <KanbanCard
                key={item.id}
                item={item}
                index={index}
                isUpdating={updatingItemId === item.id}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
            {provided.placeholder}
            {items.length === 0 && !snapshot.isDraggingOver && (
              <div className="text-center py-8 text-muted-foreground text-xs">
                Aucun item
              </div>
            )}
          </CardContent>
        )}
      </Droppable>
    </Card>
  );
}

function KanbanSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="border-t-4 border-t-muted">
          <CardHeader className="pb-3 px-3">
            <Skeleton className="h-5 w-24" />
          </CardHeader>
          <CardContent className="space-y-2 p-3 pt-0">
            {[1, 2, 3].map((j) => (
              <Skeleton key={j} className="h-24 w-full" />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Global Sprint Overview components ───────────────────────────

const SPRINT_STATUS_CONFIG = {
  overdue: {
    label: 'En retard',
    badgeClass: 'bg-red-500/10 text-red-600 border-red-200 dark:border-red-800',
    borderClass: 'border-t-red-500',
    barClass: 'bg-red-500',
  },
  'ending-soon': {
    label: 'Se termine bientôt',
    badgeClass: 'bg-orange-500/10 text-orange-600 border-orange-200 dark:border-orange-800',
    borderClass: 'border-t-orange-400',
    barClass: 'bg-orange-400',
  },
  active: {
    label: 'En cours',
    badgeClass: 'bg-blue-500/10 text-blue-600 border-blue-200 dark:border-blue-800',
    borderClass: 'border-t-blue-500',
    barClass: 'bg-blue-500',
  },
  completed: {
    label: 'Terminé',
    badgeClass: 'bg-green-500/10 text-green-600 border-green-200 dark:border-green-800',
    borderClass: 'border-t-green-500',
    barClass: 'bg-green-500',
  },
  past: {
    label: 'Passé',
    badgeClass: 'bg-gray-500/10 text-gray-500 border-gray-200 dark:border-gray-700',
    borderClass: 'border-t-gray-400',
    barClass: 'bg-gray-400',
  },
  upcoming: {
    label: 'À venir',
    badgeClass: 'bg-purple-500/10 text-purple-600 border-purple-200 dark:border-purple-800',
    borderClass: 'border-t-purple-500',
    barClass: 'bg-purple-400',
  },
  'no-dates': {
    label: 'Sans dates',
    badgeClass: 'bg-gray-500/10 text-gray-500 border-gray-200 dark:border-gray-700',
    borderClass: 'border-t-gray-300',
    barClass: 'bg-gray-300',
  },
};

function getSprintStatus(sprint) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = sprint.start_date ? new Date(sprint.start_date) : null;
  const endDate = sprint.finish_date ? new Date(sprint.finish_date) : null;

  if (!startDate || !endDate) return 'no-dates';
  if (endDate < today) {
    if (sprint.stats && sprint.stats.progressPercent === 100) return 'completed';
    return sprint.stats ? 'overdue' : 'past';
  }
  if (startDate > today) return 'upcoming';
  const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  return daysLeft <= 3 ? 'ending-soon' : 'active';
}

function SprintOverviewCard({ sprint, onViewKanban, onLoadStats }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = sprint.start_date ? new Date(sprint.start_date) : null;
  const endDate = sprint.finish_date ? new Date(sprint.finish_date) : null;
  const status = getSprintStatus(sprint);
  const cfg = SPRINT_STATUS_CONFIG[status];

  // Time progress
  let timeProgress = 0;
  let daysInfo = null;

  if (startDate && endDate) {
    if (status === 'past' || status === 'overdue' || status === 'completed') {
      timeProgress = 100;
      const daysAgo = Math.ceil((today - endDate) / (1000 * 60 * 60 * 24));
      if (daysAgo > 0) {
        daysInfo = {
          text: `Terminé il y a ${daysAgo}j`,
          className: status === 'overdue' ? 'text-red-500' : 'text-muted-foreground',
        };
      }
    } else if (status === 'active' || status === 'ending-soon') {
      const total = endDate - startDate;
      const elapsed = today - startDate;
      timeProgress = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
      const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      daysInfo = {
        text: `${daysLeft}j restant${daysLeft > 1 ? 's' : ''}`,
        className: status === 'ending-soon' ? 'text-orange-500 font-semibold' : 'text-foreground',
      };
    } else if (status === 'upcoming') {
      const daysUntil = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
      daysInfo = {
        text: `Démarre dans ${daysUntil}j`,
        className: 'text-purple-600',
      };
    }
  }

  const formatDate = (date) => {
    if (!date) return '—';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Sprint duration in days
  const durationDays =
    startDate && endDate
      ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
      : null;

  return (
    <Card className={`border-t-4 ${cfg.borderClass} flex flex-col`}>
      <CardContent className="p-4 flex flex-col gap-3 flex-1">
        {/* Header: name + badges */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight truncate" title={sprint.name}>
              {sprint.name}
            </p>
            <Badge
              variant="outline"
              className="mt-1.5 text-[10px] font-normal max-w-full truncate"
            >
              {sprint.projectName}
            </Badge>
          </div>
          <Badge
            variant="outline"
            className={`text-[10px] shrink-0 border ${cfg.badgeClass}`}
          >
            {cfg.label}
          </Badge>
        </div>

        {/* Timeline */}
        {startDate && endDate ? (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{formatDate(startDate)}</span>
              {daysInfo && (
                <span className={`text-xs ${daysInfo.className}`}>{daysInfo.text}</span>
              )}
              <span className="text-muted-foreground">{formatDate(endDate)}</span>
            </div>
            <div className="relative h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`absolute left-0 top-0 h-full rounded-full transition-all ${cfg.barClass}`}
                style={{ width: `${timeProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{timeProgress}% du temps écoulé</span>
              {durationDays && <span>{durationDays}j au total</span>}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">Aucune date définie</p>
        )}

        {/* Work items stats (lazy loaded) */}
        {sprint.statsLoading && (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {sprint.stats && !sprint.statsLoading && (
          <div className="space-y-1.5 pt-1 border-t border-border/50">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Progression tâches</span>
              <span className="font-semibold">
                {sprint.stats.done}/{sprint.stats.total} ({sprint.stats.progressPercent}%)
              </span>
            </div>
            <Progress value={sprint.stats.progressPercent} className="h-2" />
            <div className="flex gap-3 text-[10px]">
              <span className="text-muted-foreground">{sprint.stats.todo} à faire</span>
              <span className="text-blue-500">{sprint.stats.inProgress} en cours</span>
              <span className="text-green-500">{sprint.stats.done} terminés</span>
            </div>
            {(sprint.stats.estimatedHours > 0 || sprint.stats.completedHours > 0) && (
              <div className="flex gap-3 text-[10px] text-muted-foreground pt-0.5">
                <span>{sprint.stats.completedHours}h / {sprint.stats.estimatedHours}h estimées</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-auto pt-1">
          {!sprint.stats && !sprint.statsLoading && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs rounded-sm flex-1"
              onClick={() => onLoadStats(sprint)}
            >
              <BarChart2 className="h-3 w-3 mr-1" />
              Stats
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs rounded-sm flex-1"
            onClick={() => onViewKanban(sprint)}
          >
            <KanbanSquare className="h-3 w-3 mr-1" />
            Kanban
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function GlobalSprintSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card key={i} className="border-t-4 border-t-muted">
          <CardContent className="p-4 space-y-3">
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
            <Skeleton className="h-2 w-full" />
            <div className="flex gap-2">
              <Skeleton className="h-7 flex-1" />
              <Skeleton className="h-7 flex-1" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

const TIME_FILTERS = [
  { value: 'active', label: 'Actifs' },
  { value: 'week', label: 'Cette semaine' },
  { value: '2weeks', label: '2 semaines' },
  { value: 'month', label: 'Ce mois' },
  { value: 'overdue', label: 'En retard' },
  { value: 'all', label: 'Tous' },
];

// ─── Main component ──────────────────────────────────────────────

export default function Sprint() {
  const { getAuthHeader, user } = useAuth();
  const isReader = user?.role === 'reader';

  const [projects, setProjects] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [workItems, setWorkItems] = useState([]);

  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [selectedIteration, setSelectedIteration] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('all');

  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingIterations, setLoadingIterations] = useState(false);
  const [updatingItemId, setUpdatingItemId] = useState(null);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creatingSprint, setCreatingSprint] = useState(false);
  const [newSprintName, setNewSprintName] = useState('');
  const [newSprintStartDate, setNewSprintStartDate] = useState('');
  const [newSprintEndDate, setNewSprintEndDate] = useState('');
  const [deletingSprint, setDeletingSprint] = useState(false);

  // Edit sprint dates state
  const [showEditSprintDialog, setShowEditSprintDialog] = useState(false);
  const [editSprintStartDate, setEditSprintStartDate] = useState('');
  const [editSprintEndDate, setEditSprintEndDate] = useState('');
  const [updatingSprint, setUpdatingSprint] = useState(false);

  // Edit/Delete work item state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Global sprint overview state
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'global'
  const [globalTimeFilter, setGlobalTimeFilter] = useState('active');
  const [globalSprints, setGlobalSprints] = useState([]);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [pendingIterationPath, setPendingIterationPath] = useState(null);

  // Fetch user projects
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/azure-devops/projects`, {
        headers: getAuthHeader(),
      });
      const azureProjects = response.data.projects || [];
      setProjects(
        azureProjects.map((p) => ({ id: p.id, name: p.name }))
      );
    } catch {
      toast.error('Erreur lors du chargement des projets');
    } finally {
      setLoadingPage(false);
    }
  }, [getAuthHeader]);

  // Fetch iterations for selected project
  const fetchIterations = useCallback(
    async (projectName) => {
      setLoadingIterations(true);
      setIterations([]);
      setSelectedIteration('');
      try {
        const response = await axios.get(
          `${API_URL}/api/azure-devops/projects/${encodeURIComponent(projectName)}/iterations`,
          { headers: getAuthHeader() }
        );
        setIterations(response.data.iterations || []);
      } catch {
        toast.error('Erreur lors du chargement des sprints');
      } finally {
        setLoadingIterations(false);
      }
    },
    [getAuthHeader]
  );

  // Fetch team members for selected project
  const fetchTeamMembers = useCallback(
    async (projectName) => {
      try {
        const response = await axios.get(
          `${API_URL}/api/azure-devops/projects/${encodeURIComponent(projectName)}/team-members`,
          { headers: getAuthHeader() }
        );
        setTeamMembers(response.data.members || []);
      } catch {
        // Silent fail, filter just won't be populated
      }
    },
    [getAuthHeader]
  );

  // Fetch work items for selected sprint
  const fetchWorkItems = useCallback(async () => {
    if (!selectedProjectName || !selectedIteration) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/work-items`,
        {
          headers: getAuthHeader(),
          params: { iteration_path: selectedIteration },
        }
      );
      setWorkItems(response.data.work_items || []);
    } catch {
      toast.error('Erreur lors du chargement des tâches');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectName, selectedIteration, getAuthHeader]);

  // Fetch ALL sprints across all projects (global view)
  const fetchGlobalSprints = useCallback(async () => {
    if (projects.length === 0) return;
    setLoadingGlobal(true);
    try {
      const results = await Promise.allSettled(
        projects.map(async (project) => {
          const response = await axios.get(
            `${API_URL}/api/azure-devops/projects/${encodeURIComponent(project.name)}/iterations`,
            { headers: getAuthHeader() }
          );
          return {
            projectId: project.id,
            projectName: project.name,
            iterations: response.data.iterations || [],
          };
        })
      );

      const allSprints = [];
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { projectId, projectName, iterations: iters } = result.value;
          iters.forEach((it) => {
            allSprints.push({
              ...it,
              projectId,
              projectName,
              key: `${projectId}_${it.id}`,
              stats: null,
              statsLoading: false,
            });
          });
        }
      });

      setGlobalSprints(allSprints);
    } catch {
      toast.error('Erreur lors du chargement des sprints');
    } finally {
      setLoadingGlobal(false);
    }
  }, [projects, getAuthHeader]);

  // Fetch work item stats for a single sprint (global view)
  const fetchSprintStats = useCallback(
    async (sprint) => {
      setGlobalSprints((prev) =>
        prev.map((s) => (s.key === sprint.key ? { ...s, statsLoading: true } : s))
      );
      try {
        const response = await axios.get(
          `${API_URL}/api/azure-devops/projects/${encodeURIComponent(sprint.projectName)}/work-items`,
          {
            headers: getAuthHeader(),
            params: { iteration_path: sprint.path },
          }
        );
        const items = response.data.work_items || [];
        const done = items.filter((i) =>
          ['Done', 'Closed', 'Resolved'].includes(i.state)
        ).length;
        const stats = {
          total: items.length,
          done,
          inProgress: items.filter((i) =>
            ['Active', 'In Progress'].includes(i.state)
          ).length,
          todo: items.filter((i) => ['New'].includes(i.state)).length,
          progressPercent:
            items.length > 0 ? Math.round((done / items.length) * 100) : 0,
          estimatedHours: items.reduce((s, i) => s + (i.estimated_hours || 0), 0),
          completedHours: items.reduce((s, i) => s + (i.completed_hours || 0), 0),
        };
        setGlobalSprints((prev) =>
          prev.map((s) =>
            s.key === sprint.key ? { ...s, stats, statsLoading: false } : s
          )
        );
      } catch {
        setGlobalSprints((prev) =>
          prev.map((s) =>
            s.key === sprint.key ? { ...s, statsLoading: false } : s
          )
        );
        toast.error('Erreur lors du chargement des statistiques');
      }
    },
    [getAuthHeader]
  );

  // Load stats for all visible sprints at once
  const handleLoadAllStats = useCallback(() => {
    filteredGlobalSprints
      .filter((s) => !s.stats && !s.statsLoading)
      .forEach((sprint) => fetchSprintStats(sprint));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSprints, globalTimeFilter]);

  // Switch to kanban for a given sprint
  const handleViewKanban = useCallback(
    (sprint) => {
      setViewMode('kanban');
      if (selectedProjectName === sprint.projectName) {
        setSelectedIteration(sprint.path);
      } else {
        const project = projects.find((p) => p.name === sprint.projectName);
        if (project) {
          setSelectedProject(project.id);
          setSelectedProjectName(sprint.projectName);
          setPendingIterationPath(sprint.path);
        }
      }
    },
    [projects, selectedProjectName]
  );

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProjectName) {
      fetchIterations(selectedProjectName);
      fetchTeamMembers(selectedProjectName);
    }
  }, [selectedProjectName, fetchIterations, fetchTeamMembers]);

  useEffect(() => {
    if (selectedProjectName && selectedIteration) {
      fetchWorkItems();
    }
  }, [selectedProjectName, selectedIteration, fetchWorkItems]);

  // Auto-select pending iteration after iterations load
  useEffect(() => {
    if (pendingIterationPath && iterations.length > 0) {
      const match = iterations.find((it) => it.path === pendingIterationPath);
      if (match) {
        setSelectedIteration(match.path);
        setPendingIterationPath(null);
      }
    }
  }, [iterations, pendingIterationPath]);

  // Fetch global sprints when switching to global view
  useEffect(() => {
    if (viewMode === 'global' && projects.length > 0 && globalSprints.length === 0) {
      fetchGlobalSprints();
    }
  }, [viewMode, projects, globalSprints.length, fetchGlobalSprints]);

  const handleProjectChange = (projectId) => {
    if (projectId === 'none') return;
    setSelectedProject(projectId);
    const project = projects.find((p) => p.id === projectId);
    setSelectedProjectName(project?.name || '');
    setWorkItems([]);
    setSelectedAssignee('all');
  };

  // ─── Create Sprint ─────────────────────────────────────────

  const handleCreateSprint = async () => {
    if (!newSprintName || !newSprintStartDate || !newSprintEndDate) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setCreatingSprint(true);
    try {
      await axios.post(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/iterations`,
        {
          name: newSprintName,
          start_date: newSprintStartDate,
          finish_date: newSprintEndDate,
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Sprint "${newSprintName}" cree avec succes`);
      setShowCreateDialog(false);

      // Refresh iterations without resetting selection
      const iterResponse = await axios.get(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/iterations`,
        { headers: getAuthHeader() }
      );
      const newIterations = iterResponse.data.iterations || [];
      setIterations(newIterations);

      // Auto-select the newly created sprint
      const createdSprint = newIterations.find((it) => it.name === newSprintName);
      if (createdSprint) {
        setSelectedIteration(createdSprint.path);
      }

      // Also update global sprints state if already loaded
      if (globalSprints.length > 0 && createdSprint) {
        const project = projects.find((p) => p.name === selectedProjectName);
        setGlobalSprints((prev) => [
          ...prev,
          {
            ...createdSprint,
            projectId: project?.id,
            projectName: selectedProjectName,
            key: `${project?.id}_${createdSprint.id}`,
            stats: null,
            statsLoading: false,
          },
        ]);
      }

      setNewSprintName('');
      setNewSprintStartDate('');
      setNewSprintEndDate('');
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Erreur lors de la creation du sprint'
      );
    } finally {
      setCreatingSprint(false);
    }
  };

  // ─── Edit Sprint Dates ──────────────────────────────────────

  const openEditSprintDialog = () => {
    const sprint = iterations.find((it) => it.path === selectedIteration);
    if (!sprint) return;
    const toDateInput = (iso) => {
      if (!iso) return '';
      return iso.substring(0, 10);
    };
    setEditSprintStartDate(toDateInput(sprint.start_date));
    setEditSprintEndDate(toDateInput(sprint.finish_date));
    setShowEditSprintDialog(true);
  };

  const handleEditSprint = async () => {
    if (!editSprintStartDate || !editSprintEndDate) {
      toast.error('Veuillez renseigner les deux dates');
      return;
    }
    const sprint = iterations.find((it) => it.path === selectedIteration);
    if (!sprint) return;
    setUpdatingSprint(true);
    try {
      await axios.patch(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/iterations/${encodeURIComponent(sprint.name)}`,
        { start_date: editSprintStartDate, finish_date: editSprintEndDate },
        { headers: getAuthHeader() }
      );
      toast.success(`Dates du sprint "${sprint.name}" mises à jour`);
      setShowEditSprintDialog(false);
      // Refresh iterations to get updated dates
      const iterResponse = await axios.get(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/iterations`,
        { headers: getAuthHeader() }
      );
      const newIterations = iterResponse.data.iterations || [];
      setIterations(newIterations);
      // Update global sprints dates if loaded
      if (globalSprints.length > 0) {
        setGlobalSprints((prev) =>
          prev.map((s) =>
            s.projectName === selectedProjectName && s.name === sprint.name
              ? { ...s, start_date: `${editSprintStartDate}T00:00:00Z`, finish_date: `${editSprintEndDate}T00:00:00Z` }
              : s
          )
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du sprint');
    } finally {
      setUpdatingSprint(false);
    }
  };

  // ─── Delete Sprint ─────────────────────────────────────────

  const handleDeleteSprint = async () => {
    const sprint = iterations.find((it) => it.path === selectedIteration);
    if (!sprint) return;
    setDeletingSprint(true);
    try {
      await axios.delete(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/iterations/${encodeURIComponent(sprint.name)}`,
        { headers: getAuthHeader() }
      );
      toast.success(`Sprint "${sprint.name}" supprime`);
      setSelectedIteration('');
      setWorkItems([]);
      // Refresh iterations
      const iterResponse = await axios.get(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/iterations`,
        { headers: getAuthHeader() }
      );
      setIterations(iterResponse.data.iterations || []);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Erreur lors de la suppression du sprint'
      );
    } finally {
      setDeletingSprint(false);
    }
  };

  // ─── Edit/Delete Work Items ─────────────────────────────────

  const openEditDialog = (item) => {
    setEditItem({
      id: item.id,
      title: item.title || '',
      description: item.description || '',
      state: item.state || 'New',
      assigned_to: item.assigned_to || '',
      completed_hours: item.completed_hours || 0,
      tags: Array.isArray(item.tags) ? item.tags.join('; ') : (item.tags || ''),
    });
    setEditDialogOpen(true);
  };

  const handleEditItem = async () => {
    if (!editItem) return;
    setUpdating(true);
    try {
      await axios.patch(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/work-items/${editItem.id}`,
        {
          title: editItem.title,
          description: editItem.description || null,
          state: editItem.state,
          assigned_to: editItem.assigned_to || null,
          completed_hours: editItem.completed_hours || null,
          tags: editItem.tags || null,
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Tâche #${editItem.id} mise à jour`);
      setEditDialogOpen(false);
      setEditItem(null);
      fetchWorkItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const openDeleteDialog = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/work-items/${deleteItem.id}`,
        { headers: getAuthHeader() }
      );
      toast.success(`Tâche #${deleteItem.id} supprimée`);
      setDeleteDialogOpen(false);
      setDeleteItem(null);
      fetchWorkItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Drag & Drop ────────────────────────────────────────────

  const handleDragEnd = useCallback(
    async (result) => {
      if (isReader) return;
      const { destination, source, draggableId } = result;
      if (!destination) return;
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      )
        return;

      const targetColumn = KANBAN_COLUMNS.find(
        (col) => col.id === destination.droppableId
      );
      const newState = COLUMN_TARGET_STATE[destination.droppableId];
      const workItemId = parseInt(draggableId);
      const workItem = workItems.find((item) => item.id === workItemId);

      if (!workItem || !newState) return;
      if (targetColumn.states.includes(workItem.state)) return;

      // Optimistic update
      const previousItems = [...workItems];
      setWorkItems((prev) =>
        prev.map((item) =>
          item.id === workItemId ? { ...item, state: newState } : item
        )
      );
      setUpdatingItemId(workItemId);

      try {
        const response = await axios.patch(
          `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProjectName)}/work-items/${workItemId}`,
          { state: newState },
          { headers: getAuthHeader() }
        );
        // Update with the actual state returned by the backend (may differ from requested)
        const actualState = response.data.state;
        if (actualState && actualState !== newState) {
          setWorkItems((prev) =>
            prev.map((item) =>
              item.id === workItemId ? { ...item, state: actualState } : item
            )
          );
        }
        toast.success(`#${workItemId} → ${targetColumn.title}`);
      } catch (error) {
        setWorkItems(previousItems);
        toast.error(
          error.response?.data?.detail || 'Erreur lors du changement de statut'
        );
      } finally {
        setUpdatingItemId(null);
      }
    },
    [isReader, workItems, selectedProjectName, getAuthHeader]
  );

  // ─── Computed ───────────────────────────────────────────────

  const filteredItems = workItems.filter((item) => {
    if (selectedAssignee && selectedAssignee !== 'all') {
      return item.assigned_to === selectedAssignee;
    }
    return true;
  });

  const getColumnItems = (column) =>
    filteredItems.filter((item) => column.states.includes(item.state));

  const stats = {
    total: filteredItems.length,
    todo: filteredItems.filter((i) => ['New'].includes(i.state)).length,
    inProgress: filteredItems.filter((i) =>
      ['Active', 'In Progress'].includes(i.state)
    ).length,
    done: filteredItems.filter((i) =>
      ['Done', 'Closed', 'Resolved'].includes(i.state)
    ).length,
    totalEstimated: filteredItems.reduce(
      (s, i) => s + (i.estimated_hours || 0),
      0
    ),
    totalCompleted: filteredItems.reduce(
      (s, i) => s + (i.completed_hours || 0),
      0
    ),
    totalRemaining: filteredItems.reduce(
      (s, i) => s + (i.remaining_hours || 0),
      0
    ),
    progressPercent:
      filteredItems.length > 0
        ? Math.round(
            (filteredItems.filter((i) =>
              ['Done', 'Closed', 'Resolved'].includes(i.state)
            ).length /
              filteredItems.length) *
              100
          )
        : 0,
  };

  // Filtered & sorted global sprints
  const filteredGlobalSprints = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const filtered = globalSprints.filter((sprint) => {
      const startDate = sprint.start_date ? new Date(sprint.start_date) : null;
      const endDate = sprint.finish_date ? new Date(sprint.finish_date) : null;

      switch (globalTimeFilter) {
        case 'active':
          return startDate && endDate && startDate <= today && endDate >= today;
        case 'week': {
          const weekEnd = new Date(today);
          weekEnd.setDate(today.getDate() + 7);
          return startDate && endDate && startDate <= weekEnd && endDate >= today;
        }
        case '2weeks': {
          const twoWeeksEnd = new Date(today);
          twoWeeksEnd.setDate(today.getDate() + 14);
          return startDate && endDate && startDate <= twoWeeksEnd && endDate >= today;
        }
        case 'month': {
          const monthEnd = new Date(today);
          monthEnd.setDate(today.getDate() + 30);
          return startDate && endDate && startDate <= monthEnd && endDate >= today;
        }
        case 'overdue':
          return endDate && endDate < today;
        case 'all':
        default:
          return true;
      }
    });

    // Sort: overdue first, then by end date ascending, then upcoming by start date
    return filtered.sort((a, b) => {
      const aEnd = a.finish_date ? new Date(a.finish_date) : new Date('9999-12-31');
      const bEnd = b.finish_date ? new Date(b.finish_date) : new Date('9999-12-31');
      const aStart = a.start_date ? new Date(a.start_date) : new Date('9999-12-31');
      const bStart = b.start_date ? new Date(b.start_date) : new Date('9999-12-31');
      const aOverdue = aEnd < today;
      const bOverdue = bEnd < today;
      const aActive = aStart <= today && aEnd >= today;
      const bActive = bStart <= today && bEnd >= today;

      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      return aEnd - bEnd;
    });
  }, [globalSprints, globalTimeFilter]);

  // Global summary stats
  const globalStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      total: filteredGlobalSprints.length,
      overdue: filteredGlobalSprints.filter((s) => {
        const end = s.finish_date ? new Date(s.finish_date) : null;
        return end && end < today;
      }).length,
      endingSoon: filteredGlobalSprints.filter((s) => {
        const start = s.start_date ? new Date(s.start_date) : null;
        const end = s.finish_date ? new Date(s.finish_date) : null;
        if (!start || !end) return false;
        if (start > today || end < today) return false;
        const daysLeft = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
        return daysLeft <= 3;
      }).length,
      active: filteredGlobalSprints.filter((s) => {
        const start = s.start_date ? new Date(s.start_date) : null;
        const end = s.finish_date ? new Date(s.finish_date) : null;
        return start && end && start <= today && end >= today;
      }).length,
    };
  }, [filteredGlobalSprints]);

  // ─── Render ─────────────────────────────────────────────────

  if (loadingPage) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-extrabold">Sprint</h1>
          <p className="text-muted-foreground mt-1">
            {viewMode === 'kanban'
              ? 'Organisez vos priorites de sprint en Kanban'
              : 'Suivi global de tous les sprints en cours'}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {/* View mode toggle */}
          <div className="flex rounded-sm border border-border overflow-hidden">
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-8 px-3 gap-1.5"
              onClick={() => setViewMode('kanban')}
            >
              <KanbanSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Kanban</span>
            </Button>
            <Button
              variant={viewMode === 'global' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-8 px-3 gap-1.5"
              onClick={() => setViewMode('global')}
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Vue globale</span>
            </Button>
          </div>

          {viewMode === 'kanban' && selectedIteration && (
            <Button
              variant="outline"
              size="sm"
              onClick={fetchWorkItems}
              className="rounded-sm h-8"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          )}
          {viewMode === 'global' && (
            <Button
              variant="outline"
              size="sm"
              onClick={fetchGlobalSprints}
              className="rounded-sm h-8"
              disabled={loadingGlobal}
            >
              {loadingGlobal ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* ── KANBAN VIEW ─────────────────────────────────────────── */}
      {viewMode === 'kanban' && (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Select
                  value={selectedProject || 'none'}
                  onValueChange={handleProjectChange}
                >
                  <SelectTrigger className="w-full sm:w-[220px]">
                    <SelectValue placeholder="Projet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>
                      Selectionner un projet
                    </SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedIteration || 'none'}
                  onValueChange={setSelectedIteration}
                  disabled={!selectedProjectName || loadingIterations}
                >
                  <SelectTrigger className="w-full sm:w-[250px]">
                    <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                    <SelectValue
                      placeholder={
                        loadingIterations ? 'Chargement...' : 'Sprint'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>
                      Selectionner un sprint
                    </SelectItem>
                    {iterations.map((it) => (
                      <SelectItem key={it.id} value={it.path}>
                        {it.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedAssignee}
                  onValueChange={setSelectedAssignee}
                  disabled={!selectedIteration}
                >
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <Users className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                    <SelectValue placeholder="Membre" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les membres</SelectItem>
                    {teamMembers.map((m) => (
                      <SelectItem key={m.unique_name} value={m.display_name}>
                        {m.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedProjectName && !isReader && (
                  <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-sm self-start sm:self-auto">
                        <Plus className="h-4 w-4 mr-2" />
                        Creer un sprint
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Creer un sprint</DialogTitle>
                        <DialogDescription>
                          Creer un nouveau sprint pour le projet {selectedProjectName}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="sprint-name">Nom du sprint</Label>
                          <Input
                            id="sprint-name"
                            placeholder="Ex: Sprint 1"
                            value={newSprintName}
                            onChange={(e) => setNewSprintName(e.target.value)}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="sprint-start">Date de debut</Label>
                            <Input
                              id="sprint-start"
                              type="date"
                              value={newSprintStartDate}
                              onChange={(e) => setNewSprintStartDate(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="sprint-end">Date de fin</Label>
                            <Input
                              id="sprint-end"
                              type="date"
                              value={newSprintEndDate}
                              onChange={(e) => setNewSprintEndDate(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setShowCreateDialog(false)}
                          disabled={creatingSprint}
                        >
                          Annuler
                        </Button>
                        <Button
                          onClick={handleCreateSprint}
                          disabled={creatingSprint || !newSprintName || !newSprintStartDate || !newSprintEndDate}
                        >
                          {creatingSprint ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Plus className="h-4 w-4 mr-2" />
                          )}
                          Creer
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}

                {selectedIteration && !isReader && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm self-start sm:self-auto"
                      onClick={openEditSprintDialog}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Modifier dates
                    </Button>
                  </>
                )}

                {selectedIteration && !isReader && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm self-start sm:self-auto text-destructive hover:text-destructive"
                        disabled={deletingSprint}
                      >
                        {deletingSprint ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-2" />
                        )}
                        Supprimer
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce sprint ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Le sprint "{iterations.find((it) => it.path === selectedIteration)?.name}" sera supprime
                          d'Azure DevOps. Les tâches associées ne seront pas supprimées mais ne seront
                          plus rattachées à ce sprint.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteSprint}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Progress */}
          {filteredItems.length > 0 && (
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  {/* Progress bar */}
                  <div className="flex-1 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Progression</span>
                      <span className="text-sm font-semibold text-primary">
                        {stats.progressPercent}%
                      </span>
                    </div>
                    <Progress value={stats.progressPercent} className="h-3" />
                  </div>

                  <Separator
                    orientation="vertical"
                    className="hidden sm:block h-12"
                  />

                  {/* Counts */}
                  <div className="flex gap-4 sm:gap-6 text-center">
                    <div>
                      <p className="text-xl sm:text-2xl font-bold">{stats.total}</p>
                      <p className="text-muted-foreground text-[10px] sm:text-xs">
                        Total
                      </p>
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-gray-500">
                        {stats.todo}
                      </p>
                      <p className="text-muted-foreground text-[10px] sm:text-xs">
                        A faire
                      </p>
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-blue-500">
                        {stats.inProgress}
                      </p>
                      <p className="text-muted-foreground text-[10px] sm:text-xs">
                        En cours
                      </p>
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-green-500">
                        {stats.done}
                      </p>
                      <p className="text-muted-foreground text-[10px] sm:text-xs">
                        Termine
                      </p>
                    </div>
                  </div>

                  <Separator
                    orientation="vertical"
                    className="hidden sm:block h-12"
                  />

                  {/* Hours */}
                  <div className="flex gap-4 text-center">
                    <div>
                      <p className="text-lg font-semibold">
                        {stats.totalEstimated}h
                      </p>
                      <p className="text-muted-foreground text-[10px] sm:text-xs">
                        Estime
                      </p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold">
                        {stats.totalCompleted}h
                      </p>
                      <p className="text-muted-foreground text-[10px] sm:text-xs">
                        Complete
                      </p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold">
                        {stats.totalRemaining}h
                      </p>
                      <p className="text-muted-foreground text-[10px] sm:text-xs">
                        Restant
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Kanban Board */}
          {loading ? (
            <KanbanSkeleton />
          ) : !selectedProjectName || !selectedIteration ? (
            <Card>
              <div className="empty-state py-16">
                <KanbanSquare className="empty-state-icon" />
                <p className="empty-state-title">
                  Selectionnez un projet et un sprint
                </p>
                <p className="empty-state-description">
                  Choisissez un projet et un sprint pour afficher le tableau Kanban
                </p>
              </div>
            </Card>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {KANBAN_COLUMNS.map((column) => (
                  <KanbanColumn
                    key={column.id}
                    column={column}
                    items={getColumnItems(column)}
                    updatingItemId={updatingItemId}
                    onEdit={isReader ? null : openEditDialog}
                    onDelete={isReader ? null : openDeleteDialog}
                  />
                ))}
              </div>
            </DragDropContext>
          )}
        </>
      )}

      {/* ── GLOBAL VIEW ─────────────────────────────────────────── */}
      {viewMode === 'global' && (
        <>
          {/* Filters & summary */}
          <Card>
            <CardContent className="p-3 sm:p-4 space-y-3">
              {/* Time filter buttons */}
              <div className="flex flex-wrap gap-2">
                {TIME_FILTERS.map((f) => (
                  <Button
                    key={f.value}
                    variant={globalTimeFilter === f.value ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-sm h-8"
                    onClick={() => setGlobalTimeFilter(f.value)}
                  >
                    {f.value === 'overdue' && (
                      <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {f.value === 'active' && (
                      <Activity className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {f.label}
                  </Button>
                ))}
              </div>

              {/* Summary bar */}
              {!loadingGlobal && globalSprints.length > 0 && (
                <div className="flex items-center gap-4 pt-1 border-t border-border/50">
                  <span className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">{globalStats.total}</span> sprint{globalStats.total > 1 ? 's' : ''}
                  </span>
                  {globalStats.overdue > 0 && (
                    <span className="text-sm text-red-500 font-medium flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {globalStats.overdue} en retard
                    </span>
                  )}
                  {globalStats.endingSoon > 0 && (
                    <span className="text-sm text-orange-500 font-medium flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {globalStats.endingSoon} se termin{globalStats.endingSoon > 1 ? 'ent' : 'e'} bientôt
                    </span>
                  )}
                  {globalStats.active > 0 && (
                    <span className="text-sm text-blue-500 font-medium flex items-center gap-1">
                      <Activity className="h-3.5 w-3.5" />
                      {globalStats.active} actif{globalStats.active > 1 ? 's' : ''}
                    </span>
                  )}
                  {filteredGlobalSprints.some((s) => !s.stats && !s.statsLoading) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-sm h-7 text-xs ml-auto"
                      onClick={handleLoadAllStats}
                    >
                      <BarChart2 className="h-3.5 w-3.5 mr-1.5" />
                      Charger toutes les stats
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sprint cards */}
          {loadingGlobal ? (
            <GlobalSprintSkeleton />
          ) : globalSprints.length === 0 ? (
            <Card>
              <div className="empty-state py-16">
                <LayoutGrid className="empty-state-icon" />
                <p className="empty-state-title">Aucun sprint trouvé</p>
                <p className="empty-state-description">
                  Vérifiez votre configuration Azure DevOps ou actualisez la page
                </p>
              </div>
            </Card>
          ) : filteredGlobalSprints.length === 0 ? (
            <Card>
              <div className="empty-state py-12">
                <CalendarDays className="empty-state-icon" />
                <p className="empty-state-title">Aucun sprint pour cette période</p>
                <p className="empty-state-description">
                  Essayez un autre filtre pour voir plus de sprints
                </p>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredGlobalSprints.map((sprint) => (
                <SprintOverviewCard
                  key={sprint.key}
                  sprint={sprint}
                  onViewKanban={handleViewKanban}
                  onLoadStats={fetchSprintStats}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Edit Sprint Dates Dialog */}
      <Dialog open={showEditSprintDialog} onOpenChange={setShowEditSprintDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier les dates du sprint</DialogTitle>
            <DialogDescription>
              {iterations.find((it) => it.path === selectedIteration)?.name} — {selectedProjectName}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-sprint-start">Date de début</Label>
              <Input
                id="edit-sprint-start"
                type="date"
                value={editSprintStartDate}
                onChange={(e) => setEditSprintStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-sprint-end">Date de fin</Label>
              <Input
                id="edit-sprint-end"
                type="date"
                value={editSprintEndDate}
                onChange={(e) => setEditSprintEndDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSprintDialog(false)} disabled={updatingSprint}>
              Annuler
            </Button>
            <Button
              onClick={handleEditSprint}
              disabled={updatingSprint || !editSprintStartDate || !editSprintEndDate}
            >
              {updatingSprint ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Work Item Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier la Tâche #{editItem?.id}</DialogTitle>
            <DialogDescription>
              Les modifications seront appliquées directement sur Azure DevOps
            </DialogDescription>
          </DialogHeader>
          {editItem && (
            <form onSubmit={(e) => { e.preventDefault(); handleEditItem(); }}>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="edit-title">Titre *</Label>
                  <Input
                    id="edit-title"
                    value={editItem.title}
                    onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
                    placeholder="Titre de la tâche"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>État</Label>
                  <Select
                    value={editItem.state}
                    onValueChange={(v) => setEditItem({ ...editItem, state: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Assigné à</Label>
                  <Select
                    value={editItem.assigned_to || 'none'}
                    onValueChange={(v) => setEditItem({ ...editItem, assigned_to: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Non assigné" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non assigné</SelectItem>
                      {teamMembers.map((m) => (
                        <SelectItem key={m.unique_name} value={m.display_name}>
                          {m.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-completed-hours">Heures complétées</Label>
                  <Input
                    id="edit-completed-hours"
                    type="number"
                    min="0"
                    step="0.5"
                    value={editItem.completed_hours || 0}
                    onChange={(e) => setEditItem({ ...editItem, completed_hours: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="edit-tags">Tags</Label>
                  <Input
                    id="edit-tags"
                    value={editItem.tags}
                    onChange={(e) => setEditItem({ ...editItem, tags: e.target.value })}
                    placeholder="Tag1; Tag2; Tag3"
                  />
                  <p className="text-xs text-muted-foreground">Séparez les tags par des points-virgules (;)</p>
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editItem.description}
                    onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                    placeholder="Description de la tâche..."
                    rows={4}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={updating}>
                  {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Work Item Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la tâche #{deleteItem?.id} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La tâche sera définitivement supprimée
              d'Azure DevOps et ne pourra pas être récupérée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
